import { useCallback, useEffect, useState } from "react";
import {
  useGetAgentChatHistoryQuery,
  useGetGroupHistoryQuery,
  useGetChatReadStateQuery,
  useMarkChatReadMutation,
} from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";
import {
  GROUP_HISTORY_LIMIT,
  GROUP_HISTORY_POLL_MS,
} from "./agentChatConstants";

// localStorage is now only a fallback/cache — the server read cursors are the
// source of truth, so reading on one browser is reflected on every other one.
const PERSONAL_KEY = "agent_chat_personal_last_seen";
const GROUP_KEY = "agent_chat_group_last_seen";

function getLocalLastSeen(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setLocalLastSeen(key: string, ts: string) {
  try {
    localStorage.setItem(key, ts);
  } catch {
    // quota exceeded or private mode
  }
}

export function useUnreadCount() {
  const user = useAppSelector((s) => s.auth);
  const isLoggedIn = user.bootstrapStatus === "ready" && !!user.token;

  const { data: personalData } = useGetAgentChatHistoryQuery(
    { limit: 10 },
    { skip: !isLoggedIn, pollingInterval: 15_000 },
  );

  const { data: groupData } = useGetGroupHistoryQuery(
    { limit: GROUP_HISTORY_LIMIT },
    { pollingInterval: GROUP_HISTORY_POLL_MS },
  );

  // server-side read cursors (browser-independent); fall back to localStorage
  const { data: readState } = useGetChatReadStateQuery(undefined, {
    skip: !isLoggedIn,
  });
  const [markChatRead] = useMarkChatReadMutation();

  const [personalUnread, setPersonalUnread] = useState(0);
  const [groupUnread, setGroupUnread] = useState(0);

  const personalLastSeen =
    readState?.personalLastReadAt ?? getLocalLastSeen(PERSONAL_KEY);
  const groupLastSeen =
    readState?.groupLastReadAt ?? getLocalLastSeen(GROUP_KEY);

  // compute personal unread — count assistant messages newer than last seen
  useEffect(() => {
    if (!personalData?.messages?.length) {
      setPersonalUnread(0);
      return;
    }
    if (!personalLastSeen) {
      // first visit — mark all as unread if there are messages from the agent
      const agentMsgs = personalData.messages.filter((m) => m.role === "assistant");
      setPersonalUnread(agentMsgs.length > 0 ? 1 : 0);
      return;
    }
    const count = personalData.messages.filter(
      (m) => m.role === "assistant" && m.timestamp > personalLastSeen,
    ).length;
    setPersonalUnread(count);
  }, [personalData, personalLastSeen]);

  // compute group unread — count messages newer than last seen
  useEffect(() => {
    if (!groupData?.messages?.length) {
      setGroupUnread(0);
      return;
    }
    if (!groupLastSeen) {
      setGroupUnread(groupData.messages.length > 0 ? 1 : 0);
      return;
    }
    const count = groupData.messages.filter((m) => m.createdAt > groupLastSeen).length;
    setGroupUnread(count);
  }, [groupData, groupLastSeen]);

  // mark personal as read — persists the cursor server-side (all browsers)
  const markPersonalRead = useCallback(() => {
    const msgs = personalData?.messages;
    if (!msgs || msgs.length === 0) return;
    const latest = msgs[msgs.length - 1].timestamp;
    setLocalLastSeen(PERSONAL_KEY, latest);
    setPersonalUnread(0);
    // only hit the server when this actually advances the cursor
    if (isLoggedIn && (!personalLastSeen || latest > personalLastSeen)) {
      markChatRead({ personal: latest });
    }
  }, [personalData, personalLastSeen, isLoggedIn, markChatRead]);

  // mark group as read
  const markGroupRead = useCallback(() => {
    const msgs = groupData?.messages;
    if (!msgs || msgs.length === 0) return;
    const latest = msgs[msgs.length - 1].createdAt;
    setLocalLastSeen(GROUP_KEY, latest);
    setGroupUnread(0);
    if (isLoggedIn && (!groupLastSeen || latest > groupLastSeen)) {
      markChatRead({ group: latest });
    }
  }, [groupData, groupLastSeen, isLoggedIn, markChatRead]);

  const totalUnread = personalUnread + groupUnread;

  return {
    personalUnread,
    groupUnread,
    totalUnread,
    markPersonalRead,
    markGroupRead,
  };
}
